/**
 * Wire shapes for chart-presence SSE events — `BACKLOG.md` C11, the first real-time/push consumer.
 *
 * `GET /api/patients/:patientId/live` (`server/src/clinical/presenceRoutes.ts`) pushes these over
 * Server-Sent Events; `web/src/hooks/useChartPresence.ts` parses them. Shared here for the same
 * reason every other `*Requests.ts` pair in this package is shared: the server's `writeSSE` payload
 * and the client's parser must agree on one schema, not two hand-copied shapes that can drift.
 *
 * Kept intentionally small — this is presence (who else has this chart open), not a general
 * event bus. A `resourceType` field is deliberately not part of this shape; if a second event kind
 * (e.g. "note signed") is added later, it gets its own schema here rather than this one growing an
 * ad hoc discriminated union for a single consumer.
 */

import { z } from 'zod';

/** One connected viewer. `connectionId` (not just `userId`) so the same user open in two tabs shows
 * as two entries the server can independently join/leave — the client is what decides whether to
 * de-duplicate by `userId` for display. */
export const presenceViewerSchema = z.strictObject({
  userId: z.uuid(),
  connectionId: z.uuid(),
});
export type PresenceViewer = z.infer<typeof presenceViewerSchema>;

/** The `snapshot` event a connection receives once, immediately after subscribing: every other
 * viewer already present in the room. Never includes the subscriber's own connection. */
export const presenceSnapshotEventSchema = z.strictObject({
  viewers: z.array(presenceViewerSchema),
});
export type PresenceSnapshotEvent = z.infer<typeof presenceSnapshotEventSchema>;

/** The payload of both `join` and `leave` events — the one viewer who joined or left. */
export const presenceChangeEventSchema = presenceViewerSchema;
export type PresenceChangeEvent = PresenceViewer;

/** The SSE `event:` field values `presenceRoutes.ts` writes. `heartbeat` carries no schema-checked
 * payload — its `data` is a plain timestamp string, sent only to keep an idle connection open
 * through an intermediary that would otherwise time it out, and the client does not act on it. */
export const PRESENCE_SSE_EVENT_NAMES = ['snapshot', 'join', 'leave', 'heartbeat'] as const;
export type PresenceSseEventName = (typeof PRESENCE_SSE_EVENT_NAMES)[number];
